<template>
  <div class="outpatient-container">
    <!-- ========== 1. 搜索栏 ========== -->
    <el-card shadow="never" class="search-card">
      <el-form
          :model="searchForm"
          size="small"
          label-width="70px"
          label-suffix="："
          class="search-form"
      >
        <el-row :gutter="12">
          <el-col :span="6">
            <el-form-item label="患者姓名">
              <el-input
                  v-model="searchForm.patientName"
                  placeholder="姓名"
                  clearable
                  @keyup.enter.native="handleSearch"
                  @clear="handleSearch"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item label="日期起">
              <el-date-picker
                  v-model="searchForm.startTime"
                  type="date"
                  placeholder="开始日期"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                  @change="handleSearch"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item label="日期止">
              <el-date-picker
                  v-model="searchForm.endTime"
                  type="date"
                  placeholder="结束日期"
                  value-format="yyyy-MM-dd"
                  style="width: 100%"
                  @change="handleSearch"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item label="医生">
              <el-select
                  v-model="searchForm.doctorId"
                  placeholder="全部"
                  clearable
                  style="width: 100%"
                  @change="handleSearch"
              >
                <el-option label="全部" :value="undefined" />
                <el-option
                    v-for="item in doctorOptions"
                    :key="item.id"
                    :label="item.label"
                    :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- ========== 2. 表格区域 ========== -->
    <el-card shadow="never" class="table-card">

      <!-- 顶部工具栏 -->
      <div class="table-toolbar">
        <div class="toolbar-left">
          <el-radio-group v-model="viewTab" size="small" @change="handleTabChange">
            <el-radio-button label="time">全部</el-radio-button>
            <el-radio-button label="patient">按患者</el-radio-button>
          </el-radio-group>
          <span class="record-count">共 <b>{{ viewTab === 'time' ? totalSize : patientTotalSize }}</b> 条诊疗记录</span>
        </div>
        <div class="toolbar-right">
          <el-button type="default" size="small" icon="el-icon-download" @click="handleExport">导出Excel</el-button>
        </div>
      </div>

      <!-- 2.1 按时间模式：原来的平铺表格 -->
      <template v-if="viewTab === 'time'">
        <el-table
            v-loading="loading"
            :data="tableData"
            stripe
            border
            size="small"
            style="width: 100%"
            :header-cell-style="{ background: '#fafafa', color: '#606266', fontWeight: 500 }"
            @row-dblclick="handleViewDetail"
        >
          <el-table-column label="就诊日期" width="140" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.createTime) }}
            </template>
          </el-table-column>
          <el-table-column label="患者姓名" width="100" align="center">
            <template slot-scope="scope">
              <el-link type="primary" :underline="false">{{ scope.row.patientName }}</el-link>
            </template>
          </el-table-column>
          <el-table-column label="性别" width="60" align="center">
            <template slot-scope="scope">{{ formatGender(scope.row.gender) }}</template>
          </el-table-column>
          <el-table-column prop="age" label="年龄" width="60" align="center" />
          <el-table-column label="接诊医生" width="100" align="center">
            <template slot-scope="scope">{{ formatDoctorName(scope.row.doctorId) }}</template>
          </el-table-column>
          <el-table-column label="西医诊断" min-width="150" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.westernDiagnos || '—' }}</template>
          </el-table-column>
          <el-table-column label="中医诊断" min-width="150" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.chinaDiagnose || '—' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)" size="mini" effect="light">
                {{ formatStatus(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="handleViewDetail(scope.row)">详情</el-button>
              <el-button type="text" size="small" @click="handleExportRow(scope.row)">导出</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
              background
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="searchForm.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="searchForm.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="totalSize"
          />
        </div>
      </template>

      <!-- 2.2 按患者模式：三栏联动布局 -->
      <div v-else class="patient-view-container">

        <!-- 左侧：患者列表 -->
        <div class="patient-sidebar">
          <div class="sidebar-search">
            <el-input
                v-model="patientSearchName"
                placeholder="按姓名过滤（回车查询）"
                size="small"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handlePatientSearch"
                @clear="handlePatientSearch"
            />
          </div>
          <div class="patient-list" v-loading="patientLoading">
            <div
                v-for="patient in patientList"
                :key="patient.patientId"
                class="patient-item"
                :class="{ active: currentPatient && currentPatient.patientId === patient.patientId }"
                @click="handleSelectPatient(patient)"
            >
              <div class="patient-info">
                <div class="p-name">{{ patient.patientName }}</div>
                <div class="p-meta">{{ formatGender(patient.gender) }} · {{ patient.age }}岁</div>
              </div>
              <div class="p-times">{{ patient.times }} 次</div>
            </div>
            <el-empty v-if="patientList.length === 0 && !patientLoading" description="暂无患者" :image-size="60"></el-empty>
          </div>
          <div class="sidebar-pagination">
            <el-pagination
                small
                layout="prev, pager, next"
                :total="patientTotalSize"
                :page-size="patientSearchForm.pageSize"
                :current-page="patientSearchForm.pageNum"
                @current-change="handlePatientPageChange"
            />
          </div>
        </div>

        <!-- 中间：就诊时间轴 -->
        <div class="visit-timeline" v-loading="visitLoading">
          <template v-if="currentPatient">
            <div class="timeline-header">
              <h3>{{ currentPatient.patientName }}</h3>
              <p>{{ formatGender(currentPatient.gender) }} {{ currentPatient.age }}岁 · 共 {{ currentPatient.times }} 次就诊</p>
            </div>
            <el-timeline>
              <el-timeline-item
                  v-for="(visit, index) in visitList"
                  :key="visit.id"
                  :type="currentVisit && currentVisit.id === visit.id ? 'primary' : ''"
                  :color="currentVisit && currentVisit.id === visit.id ? '#409EFF' : '#E4E7ED'"
                  placement="top"
              >
                <div
                    class="timeline-card"
                    :class="{ active: currentVisit && currentVisit.id === visit.id }"
                    @click="handleSelectVisit(visit)"
                >
                  <div class="tc-header">
                    <span class="tc-time">{{ formatDateTime(visit.createTime) }}</span>
                    <el-tag :type="getStatusType(visit.status)" size="mini" effect="light" style="transform: scale(0.9);">
                      {{ formatStatus(visit.status) }}
                    </el-tag>
                  </div>
                  <div class="tc-body">
                    <p>主诉：{{ visit.patientTell || '—' }}</p>
                    <p>诊断：{{ visit.westernDiagnos || visit.chinaDiagnose || '—' }}</p>
                  </div>
                </div>
              </el-timeline-item>
            </el-timeline>
            <el-empty v-if="visitList.length === 0 && !visitLoading" description="暂无就诊记录" :image-size="60"></el-empty>
          </template>
          <el-empty v-else description="请从左侧选择患者" :image-size="80"></el-empty>
        </div>

        <!-- 右侧：就诊详情与处方 -->
        <div class="visit-detail-panel" v-loading="visitDetailLoading">
          <template v-if="currentVisit">
            <div class="detail-header">
              <h2>{{ formatDateTime(currentVisit.createTime) }}</h2>
              <div class="sub-header">
                {{ formatDeptName(currentVisit.regDepId || currentVisit.deptId) }} · 接诊医生 {{ formatDoctorName(currentVisit.doctorId) }}
              </div>
            </div>

            <div class="detail-section">
              <div class="section-title">主诉</div>
              <div class="section-content">{{ currentVisit.patientTell || '—' }}</div>
            </div>

            <div class="detail-section two-cols">
              <div>
                <div class="section-title">现病史</div>
                <div class="section-content">{{ currentVisit.nowHistory || '—' }}</div>
              </div>
              <div>
                <div class="section-title">既往史</div>
                <div class="section-content">{{ currentVisit.beforeHistory || '—' }}</div>
              </div>
              <div>
                <div class="section-title">过敏史</div>
                <div class="section-content">{{ currentVisit.allergen || '无' }}</div>
              </div>
            </div>

            <div class="detail-section">
              <div class="section-title">诊断</div>
              <div class="section-content">
                <el-tag v-if="currentVisit.westernDiagnos" type="primary" effect="plain" class="diag-tag" size="small">{{ currentVisit.westernDiagnos }}</el-tag>
                <el-tag v-if="currentVisit.chinaDiagnose" type="success" effect="plain" class="diag-tag" size="small">{{ currentVisit.chinaDiagnose }}</el-tag>
                <span v-if="!currentVisit.westernDiagnos && !currentVisit.chinaDiagnose">—</span>
              </div>
            </div>

            <!-- 缴费信息 -->
            <div class="detail-section">
              <div class="section-title">缴费信息</div>
              <div class="section-content fee-info">
                <span>总收费金额：</span>
                <span class="total-fee">¥{{ formatMoney(currentVisitTotalAmount) }}</span>
                <span style="margin-left: 16px; color: #909399; font-size: 12px;">共 {{ currentVisitOrders.length }} 笔订单</span>
              </div>
            </div>

            <!-- 处方明细 -->
            <div class="detail-section">
              <div class="section-title">处方信息</div>
              <el-table
                  :data="currentVisitPrescriptions"
                  size="mini"
                  border
                  style="width: 100%; margin-top: 6px;"
                  :header-cell-style="{ background: '#fafafa', color: '#606266', padding: '4px 0' }"
              >
                <el-table-column type="index" width="40" align="center" />
                <el-table-column prop="itemName" label="药品名称" min-width="120" show-overflow-tooltip />
                <el-table-column prop="specification" label="规格" width="80" />
                <el-table-column prop="unitName" label="单位" width="50" align="center" />
                <el-table-column prop="totalQuantity" label="数量" width="50" align="right" />
                <el-table-column label="用法用量" width="130">
                  <template slot-scope="scope">
                    <span v-if="scope.row.singleDose">
                      {{ scope.row.singleDose }}{{ scope.row.doseUnit }}/次 · {{ scope.row.frequencyCount }}次
                    </span>
                    <span v-else>—</span>
                  </template>
                </el-table-column>
                <el-table-column label="单价" width="70" align="right">
                  <template slot-scope="scope">¥{{ formatMoney(scope.row.unitPrice) }}</template>
                </el-table-column>
                <el-table-column label="金额" width="70" align="right">
                  <template slot-scope="scope">¥{{ formatMoney(scope.row.totalAmount) }}</template>
                </el-table-column>
                <el-table-column label="状态" width="70" align="center">
                  <template slot-scope="scope">
                    <el-tag size="mini" :type="scope.row.chargeStatus === 1 ? 'success' : 'info'">
                      {{ scope.row.chargeStatus === 1 ? '已发' : '未发' }}
                    </el-tag>
                  </template>
                </el-table-column>
              </el-table>
              <el-empty v-if="currentVisitPrescriptions.length === 0 && !visitDetailLoading" description="暂无处方信息" :image-size="60"></el-empty>
            </div>
          </template>
          <el-empty v-else description="请选择中间的就诊记录" :image-size="80"></el-empty>
        </div>
      </div>
    </el-card>

    <!-- ========== 3. 诊疗详情弹窗（处方 / 执行 两个 Tab） ========== -->
    <el-dialog
        title="诊疗详情"
        :visible.sync="detailVisible"
        width="1100px"
        top="6vh"
        :close-on-click-modal="false"
    >
      <el-tabs v-model="detailTab" class="detail-tabs">
        <!-- ---------- Tab 1：处方信息 ---------- -->
        <el-tab-pane name="prescription">
          <span slot="label"><i class="el-icon-document"></i> 处方信息</span>
          <div v-loading="detailLoading" class="order-wrapper">
            <el-empty
                v-if="!detailLoading && orderList.length === 0"
                description="暂无处方/订单数据"
                :image-size="100"
            />
            <div v-for="(order, orderIndex) in orderList" :key="order.id" class="order-block">
              <div class="order-header">
                <div class="order-header-left">
                  <span class="order-index">#{{ orderIndex + 1 }}</span>
                  <span class="order-no">{{ order.orderNo }}</span>
                  <el-tag :type="getOrderStatusType(order.status)" size="mini" effect="light">
                    {{ formatOrderStatus(order.status) }}
                  </el-tag>
                  <el-tag :type="order.chargeStatus === 1 ? 'success' : 'warning'" size="mini" effect="light">
                    {{ order.chargeStatus === 1 ? '已收费' : '未收费' }}
                  </el-tag>
                </div>
                <span class="order-time">开单时间：{{ formatDateTime(order.orderedAt, 19) }}</span>
              </div>
              <el-descriptions :column="3" size="small" border class="order-desc">
                <el-descriptions-item label="患者姓名">{{ order.patientName || '—' }}</el-descriptions-item>
                <el-descriptions-item label="开单科室">{{ formatDeptName(order.orderingDeptId) }}</el-descriptions-item>
                <el-descriptions-item label="开单医生">{{ formatDoctorName(order.orderingDoctorId) }}</el-descriptions-item>
                <el-descriptions-item label="诊断名称" :span="3">{{ order.diagnosisName || '—' }}</el-descriptions-item>
              </el-descriptions>
              <el-table
                  :data="order.items"
                  border
                  size="mini"
                  style="width: 100%"
                  :header-cell-style="{ background: '#fafafa', color: '#606266', fontWeight: 500 }"
              >
                <el-table-column label="项目名称" prop="itemName" min-width="140" show-overflow-tooltip />
                <el-table-column label="类型" width="80" align="center">
                  <template slot-scope="scope">{{ formatItemType(scope.row.itemType) }}</template>
                </el-table-column>
                <el-table-column label="数量" width="100" align="right">
                  <template slot-scope="scope">{{ scope.row.totalQuantity }} {{ scope.row.unitName }}</template>
                </el-table-column>
                <el-table-column label="金额" width="100" align="right">
                  <template slot-scope="scope">
                    <span style="color: #f56c6c;">¥{{ formatMoney(scope.row.totalAmount) }}</span>
                  </template>
                </el-table-column>
              </el-table>
              <div class="order-total">订单合计：<span class="total-amount">¥{{ formatMoney(order.totalAmount) }}</span></div>
            </div>
          </div>
        </el-tab-pane>

        <!-- ---------- Tab 2：执行信息 ---------- -->
        <el-tab-pane name="execution">
          <span slot="label"><i class="el-icon-s-order"></i> 执行信息</span>
          <div v-loading="executionLoading" class="execution-wrapper">

            <!-- 执行计划 -->
            <div class="exec-section">
              <div class="exec-section-title">
                <span class="exec-label"><i class="el-icon-time"></i> 执行计划</span>
                <span class="exec-count">共 {{ executionPlanList.length }} 条</span>
              </div>
              <el-table
                  :data="executionPlanList"
                  border
                  size="mini"
                  max-height="300"
                  style="width: 100%"
                  :header-cell-style="{ background: '#fafafa', color: '#606266', fontWeight: 500 }"
                  :span-method="groupSpanMethod"
                  row-key="_originalIndex"
              >
                <!-- 分组列（合并单元格） -->
                <el-table-column label="分组" width="80" align="center">
                  <template slot-scope="scope">
                    <el-tooltip
                        v-if="scope.row._isValidGroup"
                        :content="`第${scope.row._groupId}组，共${scope.row._groupSize}项，序号 ${scope.row._groupSeq}/${scope.row._groupSize}`"
                        placement="top"
                        effect="dark"
                    >
                      <span style="font-weight: 600; color: #409EFF; cursor: help;">
                        组{{ scope.row._groupId }}
                      </span>
                    </el-tooltip>
                    <span v-else style="color: #909399; font-size: 12px;">单独</span>
                  </template>
                </el-table-column>

                <el-table-column prop="itemName" label="项目名称" min-width="130" align="center" show-overflow-tooltip />
                <el-table-column prop="specification" label="规格" min-width="90" align="center" show-overflow-tooltip />
                <el-table-column label="类型" width="85" align="center">
                  <template slot-scope="scope">{{ formatItemType(scope.row.itemType) }}</template>
                </el-table-column>
                <el-table-column label="数量" width="80" align="center">
                  <template slot-scope="scope">{{ scope.row.quantity }} {{ scope.row.unitName || '' }}</template>
                </el-table-column>
                <el-table-column label="计划状态" width="90" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="getPlanStatusType(scope.row.planStatus)" size="mini">
                      {{ getPlanStatusLabel(scope.row.planStatus) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="计划执行时间" min-width="150" align="center">
                  <template slot-scope="scope">
                    {{ formatDateTime(scope.row.plannedTime) }}
                  </template>
                </el-table-column>
                <el-table-column label="开单科室" width="110" align="center">
                  <template slot-scope="scope">{{ formatDeptName(scope.row.orderingDeptId) }}</template>
                </el-table-column>
                <el-table-column label="开单医生" width="100" align="center">
                  <template slot-scope="scope">{{ formatDoctorName(scope.row.orderingDoctorId) }}</template>
                </el-table-column>
              </el-table>
              <el-empty
                  v-if="executionPlanList.length === 0 && !executionLoading"
                  description="暂无执行计划"
                  :image-size="60"
              />
            </div>

            <!-- 执行记录 -->
            <div class="exec-section" style="margin-top: 14px;">
              <div class="exec-section-title">
                <span class="exec-label"><i class="el-icon-finished"></i> 执行记录</span>
                <span class="exec-count">共 {{ executionRecordList.length }} 条</span>
              </div>
              <el-table
                  :data="executionRecordList"
                  border
                  size="mini"
                  max-height="300"
                  style="width: 100%"
                  :header-cell-style="{ background: '#fafafa', color: '#606266', fontWeight: 500 }"
                  :span-method="groupSpanMethod"
                  row-key="_originalIndex"
              >
                <!-- 分组列（合并单元格） -->
                <el-table-column label="分组" width="80" align="center">
                  <template slot-scope="scope">
                    <el-tooltip
                        v-if="scope.row._isValidGroup"
                        :content="`第${scope.row._groupId}组，共${scope.row._groupSize}项，序号 ${scope.row._groupSeq}/${scope.row._groupSize}`"
                        placement="top"
                        effect="dark"
                    >
                      <span style="font-weight: 600; color: #409EFF; cursor: help;">
                        组{{ scope.row._groupId }}
                      </span>
                    </el-tooltip>
                    <span v-else style="color: #909399; font-size: 12px;">单独</span>
                  </template>
                </el-table-column>

                <el-table-column prop="itemName" label="项目名称" min-width="130" align="center" show-overflow-tooltip />
                <el-table-column prop="specification" label="规格" min-width="90" align="center" show-overflow-tooltip />
                <el-table-column label="数量" width="80" align="center">
                  <template slot-scope="scope">{{ scope.row.quantity }} {{ scope.row.unitName || '' }}</template>
                </el-table-column>
                <el-table-column label="单次剂量" width="100" align="center">
                  <template slot-scope="scope">
                    <span v-if="scope.row.singleDose">
                      {{ scope.row.singleDose }}{{ scope.row.doseUnit || '' }}
                    </span>
                    <span v-else>—</span>
                  </template>
                </el-table-column>
                <el-table-column label="执行状态" width="90" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="getExecStatusType(scope.row.status)" size="mini">
                      {{ getExecStatusLabel(scope.row.status) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="执行科室" width="110" align="center">
                  <template slot-scope="scope">{{ formatDeptName(scope.row.executionDeptId || scope.row.orderingDeptId) }}</template>
                </el-table-column>
                <el-table-column label="执行人" width="100" align="center">
                  <template slot-scope="scope">
                    {{ scope.row.executorName || scope.row.executorId || '—' }}
                  </template>
                </el-table-column>
                <el-table-column label="执行时间" min-width="150" align="center">
                  <template slot-scope="scope">
                    {{ formatDateTime(scope.row.executedAt || scope.row.createdAt) }}
                  </template>
                </el-table-column>
              </el-table>
              <el-empty
                  v-if="executionRecordList.length === 0 && !executionLoading"
                  description="暂无执行记录"
                  :image-size="60"
              />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <div slot="footer">
        <el-button @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getFinishReception } from "@/api/reservation/outpatientPhysician";
import {
  getOrdersByPatientId,
  getExecutionPlans,
  getExecutionPlansPage
} from "@/api/reservation/outpatientChargeV2";
import { getPatientPage } from "@/api/reservation/outpatientPhysician";
import * as XLSX from 'xlsx'

export default {
  name: 'TreatmentRecordList',
  data() {
    return {
      viewTab: 'time',
      loading: false,
      doctorOptions: [],
      deptOptions: [],

      totalSize: 0,
      tableData: [],

      patientList: [],
      patientTotalSize: 0,
      patientLoading: false,
      patientSearchName: '',
      currentPatient: null,
      patientSearchForm: {
        pageNum: 1,
        pageSize: 20,
        patientName: '',
        doctorId: undefined,
        startTime: '',
        endTime: '',
        status: ''
      },

      visitList: [],
      visitLoading: false,
      currentVisit: null,

      // 右侧详情数据
      visitDetailLoading: false,
      currentVisitOrders: [],
      currentVisitPrescriptions: [],
      currentVisitTotalAmount: 0,

      searchForm: {
        pageNum: 1,
        pageSize: 10,
        patientName: '',
        startTime: '',
        endTime: '',
        doctorId: undefined,
        status: ''
      },

      // ========== 弹窗详情 ==========
      detailVisible: false,
      detailLoading: false,
      detailTab: 'prescription',     // 处方 Tab / 执行 Tab
      orderList: [],                 // 处方（订单）

      // 执行信息
      executionLoading: false,
      executionPlanList: [],         // 该患者执行计划（带分组信息）
      executionRecordList: []        // 该患者执行记录（带分组信息）
    }
  },
  created() {
    this.doctorOptions = this.$store.getters['preload/getDoctorListOptions'] || [];
    this.deptOptions = this.$store.getters['preload/getDeptListOptions'] || [];
    this.fetchData();
  },
  methods: {
    async fetchData() {
      if (this.viewTab === 'time') {
        this.fetchTimeData();
      } else {
        this.fetchPatientData();
      }
    },

    async fetchTimeData() {
      this.loading = true;
      try {
        const res = await getFinishReception(this.searchForm);
        if (res.code === 200 || res.code === 0) {
          this.tableData = res.data.content || [];
          this.totalSize = res.data.totalSize || 0;
        }
      } catch (error) {
        console.error('获取诊疗记录失败:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchPatientData() {
      this.patientLoading = true;
      try {
        // 同步顶部的搜索条件
        this.patientSearchForm.patientName = this.searchForm.patientName || this.patientSearchName;
        this.patientSearchForm.doctorId = this.searchForm.doctorId;
        this.patientSearchForm.startTime = this.searchForm.startTime;
        this.patientSearchForm.endTime = this.searchForm.endTime;
        this.patientSearchForm.status = this.searchForm.status;

        const res = await getPatientPage(this.patientSearchForm);
        if (res.code === 200 || res.code === 0) {
          this.patientList = res.data.content || [];
          this.patientTotalSize = res.data.totalSize || 0;

          if (this.patientList.length > 0) {
            this.handleSelectPatient(this.patientList[0]);
          } else {
            this.currentPatient = null;
            this.visitList = [];
            this.currentVisit = null;
            this.resetRightPanel();
          }
        }
      } catch (error) {
        console.error('获取患者列表失败:', error);
      } finally {
        this.patientLoading = false;
      }
    },

    // 左侧按姓名过滤查询
    handlePatientSearch() {
      this.patientSearchForm.pageNum = 1;
      this.fetchPatientData();
    },

    async handleSelectPatient(patient) {
      this.currentPatient = patient;
      this.visitLoading = true;
      this.currentVisit = null;
      this.visitList = [];
      this.resetRightPanel();

      try {
        const queryParams = {
          pageNum: 1,
          pageSize: 100,
          patientId: patient.patientId || patient.id,
          doctorId: this.searchForm.doctorId,
          startTime: this.searchForm.startTime,
          endTime: this.searchForm.endTime,
          status: this.searchForm.status
        };
        const res = await getFinishReception(queryParams);
        if (res.code === 200 || res.code === 0) {
          this.visitList = res.data.content || [];
          if (this.visitList.length > 0) {
            this.handleSelectVisit(this.visitList[0]);
          }
        }
      } catch (error) {
        console.error('获取患者就诊记录失败:', error);
      } finally {
        this.visitLoading = false;
      }
    },

    // ================= 核心：点击时间轴触发订单/处方查询 =================
    async handleSelectVisit(visit) {
      this.currentVisit = visit;
      this.visitDetailLoading = true;
      this.resetRightPanel();

      try {
        const res = await getOrdersByPatientId({
          patientId: this.currentPatient.patientId || this.currentPatient.id,
          visitId: visit.id,
          unpaidOnly: false
        });

        if (res.code === 200 || res.code === 0) {
          const orders = res.data || [];
          this.currentVisitOrders = orders;

          let totalAmount = 0;
          let allItems = [];

          orders.forEach(order => {
            totalAmount += Number(order.totalAmount || 0);
            if (order.items && order.items.length > 0) {
              allItems = allItems.concat(order.items);
            }
          });

          this.currentVisitTotalAmount = totalAmount;
          this.currentVisitPrescriptions = allItems;
        }
      } catch (error) {
        console.error('查询处方订单失败:', error);
        this.$message.error('获取处方信息失败');
      } finally {
        this.visitDetailLoading = false;
      }
    },

    // 重置右侧详情面板
    resetRightPanel() {
      this.currentVisitOrders = [];
      this.currentVisitPrescriptions = [];
      this.currentVisitTotalAmount = 0;
    },

    // ================= 搜索与分页 =================
    handleTabChange() {
      this.searchForm.pageNum = 1;
      this.patientSearchForm.pageNum = 1;
      this.fetchData();
    },

    handleSearch() {
      this.searchForm.pageNum = 1;
      this.patientSearchForm.pageNum = 1;
      this.fetchData();
    },

    handleSizeChange(val) {
      this.searchForm.pageSize = val;
      if (this.viewTab === 'time') this.fetchTimeData();
    },
    handleCurrentChange(val) {
      this.searchForm.pageNum = val;
      if (this.viewTab === 'time') this.fetchTimeData();
    },

    handlePatientPageChange(val) {
      this.patientSearchForm.pageNum = val;
      this.fetchPatientData();
    },

    // ================= 导出功能 =================
    async buildExportRowData(row, isPatientMode = false) {
      let medicationText = '—';
      let treatmentText = '—';
      let amountText = '0';

      try {
        const res = await getOrdersByPatientId({
          patientId: row.patientId,
          visitId: row.visitId || row.id,
          unpaidOnly: false
        });
        const orders = (res.code === 200 || res.code === 0) ? (res.data || []) : [];

        const drugSet = new Set();
        const treatSet = new Set();
        let totalAmount = 0;

        orders.forEach(order => {
          totalAmount += Number(order.totalAmount || 0);
          (order.items || []).forEach(item => {
            const type = Number(item.itemType);
            if ([1, 2].includes(type)) drugSet.add(item.itemId || item.itemName);
            if ([4, 5, 6, 7, 8].includes(type)) treatSet.add(item.itemId || item.itemName);
          });
        });

        if (drugSet.size > 0) medicationText = `${drugSet.size}种`;
        if (treatSet.size > 0) treatmentText = `${treatSet.size}项`;
        if (totalAmount > 0) amountText = totalAmount.toFixed(0);
      } catch (error) {
        console.error('获取订单数据用于导出失败:', error);
      }

      const exportStatusMap = { 1: '进行中', 2: '已退费', 3: '转诊', 4: '已完成', 0: '禁用' };
      const statusText = exportStatusMap[row.status] || '进行中';

      return [
        this.formatDateTime(row.createTime),
        row.patientName || '',
        this.formatGender(row.gender),
        row.age || '',
        this.formatDeptName(row.regDepId || row.deptId) || '—',
        this.formatDoctorName(row.doctorId),
        row.westernDiagnos || '—',
        row.chinaDiagnose || '—',
        medicationText,
        treatmentText,
        amountText,
        statusText
      ];
    },

    setAutoFitColumnWidths(wsData) {
      if (!wsData || wsData.length === 0) return [];
      const colWidths = [];
      const colCount = wsData[0].length;
      for (let colIndex = 0; colIndex < colCount; colIndex++) {
        let maxLen = 0;
        for (let rowIndex = 0; rowIndex < wsData.length; rowIndex++) {
          const cellValue = wsData[rowIndex][colIndex];
          if (cellValue !== null && cellValue !== undefined) {
            const strValue = String(cellValue);
            let visualLen = 0;
            for (let i = 0; i < strValue.length; i++) {
              visualLen += strValue.charCodeAt(i) > 255 ? 2 : 1;
            }
            if (visualLen > maxLen) maxLen = visualLen;
          }
        }
        let finalWidth = maxLen + 1.5;
        if (finalWidth < 6) finalWidth = 6;
        if (finalWidth > 60) finalWidth = 60;
        colWidths.push({ wch: finalWidth });
      }
      return colWidths;
    },

    async handleExport() {
      const listToExport = this.viewTab === 'time' ? this.tableData : this.visitList;
      if (listToExport.length === 0) {
        this.$message.warning('暂无数据可导出');
        return;
      }

      const loading = this.$loading({ lock: true, text: '正在生成导出数据...', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.6)' });
      try {
        const headers = ['就诊日期', '患者姓名', '性别', '年龄', '科室', '接诊医生', '西医诊断', '中医诊断', '处方药品', '检查治疗', '收费金额', '状态'];
        const rows = await Promise.all(listToExport.map(row => this.buildExportRowData(row, this.viewTab === 'patient')));
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = this.setAutoFitColumnWidths(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '门诊诊疗记录');
        const dateStr = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}`;
        XLSX.writeFile(wb, `门诊诊疗记录_${dateStr}.xlsx`);
        this.$message.success('导出成功');
      } catch (error) {
        this.$message.error('导出失败，请稍后重试');
      } finally {
        loading.close();
      }
    },

    async handleExportRow(row) {
      const loading = this.$loading({ lock: true, text: '正在导出数据...', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.6)' });
      try {
        const headers = ['就诊日期', '患者姓名', '性别', '年龄', '科室', '接诊医生', '西医诊断', '中医诊断', '处方药品', '检查治疗', '收费金额', '状态'];
        const singleRow = await this.buildExportRowData(row, false);
        const wsData = [headers, singleRow];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = this.setAutoFitColumnWidths(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '就诊详情');
        XLSX.writeFile(wb, `${row.patientName}_${row.createTime ? row.createTime.substring(0, 10) : '诊疗'}_记录.xlsx`);
        this.$message.success('导出成功');
      } catch (error) {
        this.$message.error('导出失败，请稍后重试');
      } finally {
        loading.close();
      }
    },

    // ================= 详情弹窗（处方 + 执行） =================
    async handleViewDetail(row) {
      this.detailVisible = true;
      this.detailTab = 'prescription';
      this.detailLoading = true;
      this.executionLoading = true;
      this.orderList = [];
      this.executionPlanList = [];
      this.executionRecordList = [];

      const patientId = row.patientId;
      const visitDate = row.createTime ? row.createTime.substring(0, 10) : '';

      // 并行加载：处方、执行计划、执行记录
      try {
        const orderPromise = getOrdersByPatientId({
          patientId,
          visitId: row.visitId || row.id,
          unpaidOnly: false
        }).catch(err => { console.error('查处方失败:', err); return { code: -1, data: [] }; });

        const planPromise = getExecutionPlans({
          patientId
        }).catch(err => { console.error('查执行计划失败:', err); return { code: -1, data: [] }; });

        const recordPromise = getExecutionPlansPage({
          pageNum: 1,
          pageSize: 200,
          patientId,
          startTime: visitDate ? `${visitDate} 00:00:00` : undefined,
          endTime: visitDate ? `${visitDate} 23:59:59` : undefined
        }).catch(err => { console.error('查执行记录失败:', err); return { code: -1, data: { content: [] } }; });

        const [orderRes, planRes, recordRes] = await Promise.all([orderPromise, planPromise, recordPromise]);

        if (orderRes.code === 200 || orderRes.code === 0) {
          this.orderList = orderRes.data || [];
        }

        // 执行计划：应用分组
        if (planRes.code === 200) {
          const rawPlans = Array.isArray(planRes.data) ? planRes.data : [];
          this.executionPlanList = this.processGroups(rawPlans);
        } else if (planRes.code === -1) {
          this.executionPlanList = [];
        }

        // 执行记录：应用分组（同一套逻辑：groupNo + plannedTime）
        if (recordRes.code === 200) {
          const rawRecords = recordRes.data?.content || [];
          this.executionRecordList = this.processGroups(rawRecords);
        } else if (recordRes.code === -1) {
          this.executionRecordList = [];
        }
      } catch (error) {
        console.error('获取详情数据失败:', error);
        this.$message.error('获取详情数据失败');
      } finally {
        this.detailLoading = false;
        this.executionLoading = false;
      }
    },

    /**
     * 通用分组处理（执行计划 / 执行记录共用）
     * 分组条件：groupNo + 标准化后的 plannedTime
     * - groupNo 存在且组内数量 > 1 → 有效分组（显示"组N"）
     * - 其他情况 → 单独（显示"单独"）
     * 排序：分组行在前（按 groupNo + plannedTime），单独行在后，组内顺序保持原顺序
     */
    processGroups(list) {
      if (!list || !Array.isArray(list) || list.length === 0) {
        return [];
      }

      const groups = {};
      list.forEach((item, index) => {
        if (!item) return;

        const groupNo = item.groupNo ?? 'noGroup';
        // 标准化 plannedTime（去 T、去毫秒、截断到秒）
        const plannedTimeKey = item.plannedTime
            ? String(item.plannedTime).replace('T', ' ').substring(0, 19)
            : 'noTime';
        const key = `${groupNo}_${plannedTimeKey}`;

        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push({ ...item, _originalIndex: index });
      });

      let groupId = 0;
      const groupedResult = [];
      const singleResult = [];
      const groupKeys = Object.keys(groups);

      // 有效分组排前面（按 key 排序保证分组相邻），单独排后面
      groupKeys.forEach((key) => {
        const items = groups[key];
        if (!items || !Array.isArray(items) || items.length === 0) {
          return;
        }

        const isValidGroup = !key.startsWith('noGroup_') && items.length > 1;

        if (isValidGroup) {
          groupId++;
          items.forEach((row, idx) => {
            if (!row) return;
            row._groupKey = key;
            row._groupId = groupId;
            row._groupSize = items.length;
            row._isGroupFirst = idx === 0;
            row._isGroupLast = idx === items.length - 1;
            row._groupSeq = idx + 1;
            row._isValidGroup = true;
            groupedResult.push(row);
          });
        } else {
          items.forEach((row) => {
            if (!row) return;
            row._groupKey = null;
            row._groupId = null;
            row._groupSize = 1;
            row._isGroupFirst = true;
            row._isGroupLast = true;
            row._groupSeq = 1;
            row._isValidGroup = false;
            singleResult.push(row);
          });
        }
      });

      return [...groupedResult, ...singleResult];
    },

    /**
     * 分组列合并单元格（两个表格共用）
     * 只合并第 0 列（分组列）
     */
    groupSpanMethod({ row, column, rowIndex, columnIndex }) {
      if (columnIndex === 0) {
        if (row._isGroupFirst) {
          return { rowspan: row._groupSize || 1, colspan: 1 };
        } else {
          return { rowspan: 0, colspan: 0 };
        }
      }
      return { rowspan: 1, colspan: 1 };
    },

    // ================= 格式化方法 =================
    /**
     * 格式化日期时间字符串
     * 兼容 "2026-10-28T06:34:00" 与 "2026-10-28 06:34:00" 格式
     * @param {string|Date} val 时间
     * @param {number} len 截取长度，默认 16 位（YYYY-MM-DD HH:mm）
     */
    formatDateTime(val, len = 16) {
      if (!val) return '—';
      let str = String(val);
      // 兼容 ISO 字符串（去 T）
      str = str.replace('T', ' ');
      // 日期使用 Date 对象传入时
      if (val instanceof Date) {
        const pad = n => String(n).padStart(2, '0');
        str = `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())} ${pad(val.getHours())}:${pad(val.getMinutes())}:${pad(val.getSeconds())}`;
      }
      return str.length > len ? str.substring(0, len) : str;
    },

    formatDeptName(regDepId) {
      if (!regDepId) return '—';
      const target = this.deptOptions.find(item => item.id === regDepId);
      return target ? target.label : '—';
    },
    formatDoctorName(doctorId) {
      if (!doctorId) return '—';
      const target = this.doctorOptions.find(item => item.id === doctorId);
      return target ? target.label : '—';
    },
    formatStatus(status) {
      const map = { 1: '就诊', 2: '取消就诊', 3: '转诊', 0: '禁用', 4: '完成' };
      return map[status] || '未知';
    },
    getStatusType(status) {
      if (status === 1) return 'primary';
      if (status === 2) return 'info';
      if (status === 3) return 'warning';
      if (status === 4) return 'success';
      return 'info';
    },
    formatOrderStatus(status) {
      const map = { 0: '草稿', 1: '已提交', 2: '已取消' };
      return map[status] || '未知';
    },
    getOrderStatusType(status) {
      if (status === 0) return 'info';
      if (status === 1) return 'success';
      if (status === 2) return 'danger';
      return 'info';
    },
    formatItemType(type) {
      const map = { 1: '西药', 2: '中药', 4: '理疗', 5: '卫材', 6: '输液/治疗', 7: '检查', 8: '检验', 9: '挂号费', 10: '其他' };
      return map[type] || '其他';
    },
    formatGender(gender) {
      const map = { '1': '男', '2': '女', '9': '未说明', '0': '未知' };
      return map[gender] || '未知';
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '0.00';
      return Number(val).toFixed(2);
    },

    // 执行计划状态
    getPlanStatusLabel(status) {
      const map = { 0: '待执行', 1: '已完成', 2: '已取消' };
      return map[status] || '未知';
    },
    getPlanStatusType(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'info' };
      return map[status] || 'info';
    },

    // 执行记录状态
    getExecStatusLabel(status) {
      const map = { 0: '待执行', 1: '执行中', 2: '已完成', 3: '部分取消', 4: '全部取消' };
      return map[status] || '未知';
    },
    getExecStatusType(status) {
      const map = { 0: 'warning', 1: 'primary', 2: 'success', 3: 'info', 4: 'danger' };
      return map[status] || 'info';
    }
  }
}
</script>

<style scoped>
/* 整体页面容器：缩小留白，紧凑 */
.outpatient-container { padding: 10px; background-color: #f0f2f5; min-height: 100vh; box-sizing: border-box; }

/* 搜索卡片：降低高度 */
.search-card { margin-bottom: 10px; }
.search-card ::v-deep .el-card__body { padding: 10px 16px 0; }
.search-form .el-form-item { margin-bottom: 10px; }
.search-form ::v-deep .el-form-item__label { white-space: nowrap; color: #606266; }

/* 表格工具栏 */
.table-card ::v-deep .el-card__body { padding: 0; }
.table-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 8px 16px; border-bottom: 1px solid #ebeef5; background: #fff; }
.toolbar-left { display: flex; align-items: center; gap: 12px; }
.record-count { font-size: 12px; color: #909399; }
.record-count b { color: #303133; margin: 0 2px; }

/* 表格样式调优 */
.table-card ::v-deep .el-table { border-left: none; border-right: none; }
.table-card ::v-deep .el-table th { padding: 6px 0; }
.table-card ::v-deep .el-table td { padding: 6px 0; }
.pagination-container { display: flex; justify-content: flex-end; padding: 8px 16px; border-top: 1px solid #ebeef5; }

/* ========== 按患者视图三栏布局 ========== */
.patient-view-container { display: flex; height: calc(100vh - 230px); background: #fff; }
.patient-sidebar { width: 220px; border-right: 1px solid #ebeef5; display: flex; flex-direction: column; background: #fafafa; }
.sidebar-search { padding: 8px; border-bottom: 1px solid #ebeef5; }
.patient-list { flex: 1; overflow-y: auto; }
.patient-item { padding: 8px 12px; border-bottom: 1px solid #ebeef5; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: background 0.2s; }
.patient-item:hover { background: #f5f7fa; }
.patient-item.active { background: #e6f1fc; border-left: 2px solid #409eff; }
.patient-info .p-name { font-size: 13px; font-weight: 600; color: #303133; margin-bottom: 2px; }
.patient-info .p-meta { font-size: 11px; color: #909399; }
.p-times { font-size: 11px; color: #409eff; font-weight: 500; background: #ecf5ff; padding: 1px 5px; border-radius: 8px; }
.sidebar-pagination { padding: 6px; text-align: center; border-top: 1px solid #ebeef5; background: #fff; }

/* 中间时间轴 */
.visit-timeline { width: 280px; border-right: 1px solid #ebeef5; overflow-y: auto; padding: 10px; background: #fff; }
.timeline-header { margin-bottom: 10px; }
.timeline-header h3 { margin: 0 0 2px 0; font-size: 15px; color: #303133; }
.timeline-header p { margin: 0; font-size: 11px; color: #909399; }
.timeline-card { border: 1px solid #ebeef5; border-radius: 4px; padding: 8px; cursor: pointer; transition: all 0.2s; }
.timeline-card:hover { border-color: #c6e2ff; box-shadow: 0 1px 6px 0 rgba(0,0,0,.05); }
.timeline-card.active { border-color: #409eff; background: #f0f7ff; }
.tc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.tc-time { font-size: 12px; font-weight: 600; color: #303133; }
.tc-body p { margin: 2px 0; font-size: 11px; color: #606266; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* 右侧详情面板 */
.visit-detail-panel { flex: 1; overflow-y: auto; padding: 12px; background: #fff; }
.detail-header { border-bottom: 1px solid #ebeef5; padding-bottom: 8px; margin-bottom: 12px; }
.detail-header h2 { margin: 0 0 4px 0; font-size: 16px; color: #303133; }
.sub-header { font-size: 12px; color: #909399; }
.detail-section { margin-bottom: 12px; }
.section-title { font-size: 13px; font-weight: 600; color: #303133; margin-bottom: 6px; padding-left: 6px; border-left: 3px solid #409eff; }
.section-content { font-size: 12px; color: #606266; background: #fafafa; padding: 6px 10px; border-radius: 4px; min-height: 30px; line-height: 1.4; }
.two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.two-cols .section-content { background: transparent; padding: 0; }
.diag-tag { margin-right: 6px; margin-bottom: 4px; }

/* 缴费信息样式 */
.fee-info { display: flex; align-items: baseline; }
.total-fee { color: #f56c6c; font-size: 18px; font-weight: 600; margin-left: 4px; }

/* ================= 详情弹窗（Tab 版） ================= */
.detail-tabs ::v-deep .el-tabs__header {
  margin-bottom: 10px;
}
.detail-tabs ::v-deep .el-tabs__item {
  font-size: 14px;
  height: 40px;
  line-height: 40px;
}

/* 处方 Tab */
.order-wrapper { max-height: 60vh; overflow-y: auto; padding-right: 4px; }
.order-block { border: 1px solid #ebeef5; border-radius: 4px; padding: 12px; margin-bottom: 12px; background: #fff; }
.order-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.order-header-left { display: flex; align-items: center; gap: 8px; }
.order-index { width: 18px; height: 18px; line-height: 18px; text-align: center; border-radius: 50%; background: #409eff; color: #fff; font-size: 11px; }
.order-no { font-size: 13px; font-weight: 600; color: #303133; }
.order-time { font-size: 11px; color: #909399; }
.order-total { text-align: right; margin-top: 8px; font-size: 13px; color: #606266; }
.order-total .total-amount { color: #f56c6c; font-size: 16px; font-weight: 600; }

/* 执行 Tab */
.execution-wrapper { max-height: 60vh; overflow-y: auto; padding-right: 4px; }
.exec-section { background: #fff; }
.exec-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}
.exec-section-title .exec-label {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.exec-section-title .exec-label i {
  color: #409eff;
  margin-right: 4px;
}
.exec-section-title .exec-count {
  font-size: 12px;
  color: #909399;
}
</style>